// ===========================================
// Registration fees paid, week by week (#374 P6)
// ===========================================
// Every registration fee paid in a Monday-to-Sunday week (the server's school-day week), and what
// has happened to it since. The first load sends no dates (the server's last complete week); after
// that the page sends the Mon-Sun it is showing. Week arithmetic is on YYYY-MM-DD keys in UTC, never
// the browser's "today": the school's today is the report's `asOf`.
//
// The week on screen is the page's OWN state (adopted from the first response, then moved by the
// buttons), so Previous / Next / This week keep working while a week loads or when its request fails.
//
// Gated by registration_fees.read (route guard PERMISSION_SUBPATHS prefix, the nav item, the
// query's `enabled`), NOT by `reports`. State words mirror the CSV export
// (REGISTRATION_FEE_STATE_LABELS, pinned by a parity test).

import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { usePermissions } from '../../../hooks/usePermissions';
import { useExportRegistrationFeesWeekly, useRegistrationFeesWeekly } from '../../../hooks/useReports';
import type { RegistrationFeesWeeklyParams, WeeklyFeeRow, WeeklyFeeState } from '../../../api/reports.api';
import { formatCurrencyFromDollars } from '../../../utils/currency';
import { apiErrorMessage } from '../../../utils/apiError';
import { PAYMENT_METHOD_LABELS, formatPaymentDay } from '../../../utils/invoiceDisplay';
import { useSchoolTimezone } from '../../../contexts/TenantContext';

/** Mirrors the CSV export's words exactly (backend REGISTRATION_FEE_STATE_LABELS). */
const STATE_LABELS: Record<WeeklyFeeState, string> = {
  HELD: 'Held',
  APPLIED: 'Applied',
  NOT_CREDITED: 'Paid (not credited)',
  FORFEITED: 'Not proceeding',
  REFUNDED: 'Refunded',
  UNASSIGNED: 'No child assigned',
  NOT_LINKED: 'Not linked to a family',
  REFUND_FLAGGED: 'Refund flagged',
};

const STATE_COLOR: Record<WeeklyFeeState, 'default' | 'success' | 'warning' | 'info'> = {
  HELD: 'info',
  APPLIED: 'success',
  NOT_CREDITED: 'default',
  FORFEITED: 'default',
  REFUNDED: 'default',
  UNASSIGNED: 'warning',
  NOT_LINKED: 'warning',
  REFUND_FLAGGED: 'warning',
};

const nowrap = { whiteSpace: 'nowrap' } as const;

/** A school-day key (YYYY-MM-DD) as dd/mm/yyyy, without a Date object. */
const dayKeyToAu = (key: string) => key.split('-').reverse().join('/');

/** Shift a YYYY-MM-DD key by whole days (UTC arithmetic, DST-proof). */
function addDays(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** The Monday on or before a key. */
function mondayOf(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addDays(key, -((dow + 6) % 7));
}

const methodLabel = (method: string) =>
  (PAYMENT_METHOD_LABELS as Record<string, string>)[method] ?? method;

export default function RegistrationFeesWeeklyPage() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const { can } = usePermissions();
  const canRead = can('registration_fees', 'read');
  // undefined = the server's default week (first load)
  const [range, setRange] = useState<RegistrationFeesWeeklyParams | undefined>(undefined);
  const [anchor, setAnchor] = useState<{ from: string; to: string; asOf: string } | null>(null);
  const { data, isLoading, isError, error, isPlaceholderData, refetch } = useRegistrationFeesWeekly(
    range ?? {},
    canRead
  );
  const exportMutation = useExportRegistrationFeesWeekly();
  const timeZone = useSchoolTimezone();

  const dataFrom = data?.period.from;
  const dataTo = data?.period.to;
  const dataAsOf = data?.asOf;
  useEffect(() => {
    if (dataFrom && dataTo && dataAsOf) {
      setAnchor((prev) =>
        prev && prev.from === dataFrom && prev.to === dataTo && prev.asOf === dataAsOf
          ? prev
          : { from: dataFrom, to: dataTo, asOf: dataAsOf }
      );
    }
  }, [dataFrom, dataTo, dataAsOf]);

  if (!canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">You do not have permission to open the registration fees report.</Alert>
      </Box>
    );
  }

  const money = (v: string) => formatCurrencyFromDollars(v);
  const base = `/${schoolSlug}/admin`;

  const goTo = (from: string) => setRange({ from, to: addDays(from, 6) });
  // The week being shown, or the one requested while it loads.
  const period = range?.from && range.to ? { from: range.from, to: range.to } : anchor;
  const nextDisabled = !anchor || !period || anchor.asOf <= period.to; // at the school's current week (or beyond)
  const thisWeekFrom = anchor ? mondayOf(anchor.asOf) : null;
  const atThisWeek = !!period && period.from === thisWeekFrom;
  // The previous week's data held over while the requested one loads is not shown as this week's.
  const shown = data && !isPlaceholderData ? data : null;

  const familyCell = (r: WeeklyFeeRow) =>
    r.familyName === null ? (
      <span />
    ) : r.parentId ? (
      <Link component={RouterLink} to={`${base}/families?parent=${encodeURIComponent(r.parentId)}`}>
        {r.familyName}
      </Link>
    ) : (
      <span>{r.familyName}</span>
    );

  const stateCell = (r: WeeklyFeeRow) => (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip size="small" color={STATE_COLOR[r.state]} label={STATE_LABELS[r.state]} />
      {r.state === 'APPLIED' && r.appliedInvoice?.invoiceId && (
        <Link component={RouterLink} to={`${base}/invoices/${r.appliedInvoice.invoiceId}`}>
          {r.appliedInvoice.invoiceNumber ?? 'Invoice'}
        </Link>
      )}
      {r.refundFlag?.active && (
        <Typography variant="body2" color="warning.main">
          Stripe refunded {money(r.refundFlag.amount)} on {formatPaymentDay(r.refundFlag.at, timeZone)}
        </Typography>
      )}
      {r.state === 'FORFEITED' && (r.forfeitReason || r.forfeitDetail) && (
        <Typography variant="body2" color="text.secondary">
          {[r.forfeitReason, r.forfeitDetail].filter(Boolean).join(': ')}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" gutterBottom>
            Registration fees paid
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every registration fee paid in the week, and what has happened to it since.{' '}
            <Link component={RouterLink} to={`${base}/reports/registration-fees`}>
              Fees not yet applied
            </Link>
            {' · '}
            <Link component={RouterLink} to={`${base}/reports/registration-fees/forfeited`}>
              Fees kept
            </Link>
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={exportMutation.isPending ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={() => shown && exportMutation.mutate({ from: shown.period.from, to: shown.period.to })}
          disabled={!shown || shown.rows.length === 0 || exportMutation.isPending}
          sx={{ minHeight: { xs: 44 } }}
        >
          Export CSV
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={!period}
          onClick={() => period && goTo(addDays(period.from, -7))}
          sx={{ minHeight: { xs: 44, sm: 'auto' } }}
        >
          Previous week
        </Button>
        <Typography variant="subtitle1" data-testid="week-label" sx={{ px: 1 }}>
          {period ? `Mon ${dayKeyToAu(period.from)} to Sun ${dayKeyToAu(period.to)}` : 'Loading week'}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          disabled={nextDisabled}
          onClick={() => period && goTo(addDays(period.from, 7))}
          sx={{ minHeight: { xs: 44, sm: 'auto' } }}
        >
          Next week
        </Button>
        <Button
          size="small"
          disabled={!anchor || atThisWeek}
          onClick={() => thisWeekFrom && goTo(thisWeekFrom)}
          sx={{ minHeight: { xs: 44, sm: 'auto' } }}
        >
          This week
        </Button>
      </Stack>

      {isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {apiErrorMessage(error, 'Could not load the registration fees report.')}
        </Alert>
      ) : isLoading || isPlaceholderData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !shown ? null : (
        <Stack spacing={2}>
          {!shown.creditOnFirstInvoice && (
            <Alert severity="info" data-testid="switch-off-message">
              The registration fee is not credited at this school (Settings → Onboarding → Registration fee), so
              &quot;Paid (not credited)&quot; is expected here.
            </Alert>
          )}
          <Card>
            <CardContent>
              <Typography variant="body1" sx={{ mb: 1 }} data-testid="weekly-totals">
                {shown.totals.count} {shown.totals.count === 1 ? 'fee' : 'fees'}, {money(shown.totals.amount)} paid
              </Typography>
              {shown.totals.count > 0 && (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="totals-by-method">
                    {shown.totals.byMethod.map((m) => (
                      <Chip
                        key={m.method}
                        size="small"
                        variant="outlined"
                        label={`${methodLabel(m.method)}: ${m.count} (${money(m.amount)})`}
                      />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="totals-by-state">
                    {shown.totals.byState.map((s) => (
                      <Chip
                        key={s.state}
                        size="small"
                        color={STATE_COLOR[s.state]}
                        variant="outlined"
                        label={`${STATE_LABELS[s.state]}: ${s.count} (${money(s.amount)})`}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}

              {shown.rows.length === 0 ? (
                <Alert severity="info">No registration fees were paid in this week.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={nowrap}>Paid</TableCell>
                        <TableCell>Child</TableCell>
                        <TableCell>Family</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>State</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shown.rows.map((r) => (
                        <TableRow key={r.feeId} data-testid="weekly-row">
                          <TableCell sx={nowrap}>{dayKeyToAu(r.paidOn)}</TableCell>
                          <TableCell sx={nowrap}>{r.childName ?? ''}</TableCell>
                          <TableCell sx={nowrap}>{familyCell(r)}</TableCell>
                          <TableCell align="right" sx={nowrap}>{money(r.amount)}</TableCell>
                          <TableCell sx={nowrap}>{methodLabel(r.method)}</TableCell>
                          <TableCell>{stateCell(r)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
